import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "@carbon/react";
import "./requestModal.scss";
import { useApproveFuelRequest } from "../../hooks/useApproveFuelRequest";
import { useDeclineFuelRequest } from "../../hooks/useDeclineFuelRequest";
import { Parameter, User, Calendar, Star } from "@carbon/react/icons";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";

export default function RequestModal({
  setModalItem,
  modalItem,
  setErrorQueue,
}) {
  const mutationApprove = useApproveFuelRequest();
  const mutationDecline = useDeclineFuelRequest();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const formatDate = (value) => {
    const date = new Date(value);

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  };

  const closeModal = useCallback(() => {
    setModalItem((prev) => ({ ...prev, open: false }));
  }, [setModalItem]);

  useEffect(() => {
    ReactGA.event("screen_view", {
      firebase_screen: "FuelRequestSingleModal",
      firebase_screen_class: "FuelRequest",
    });
  }, []);

  const mapBackendErrorToCode = (error) => {
    if (!error) return null;

    if (error.includes("not enough balance")) {
      return "NO_BALANCE";
    }

    if (error.includes("can not approve")) {
      return "CANNOT_APPROVE";
    }

    if (error.includes("can not cancel")) {
      return "CANNOT_DECLINE";
    }

    return "UNKNOWN";
  };

  const extractErrorsFromBulkResponse = (response, code) => {
    if (response?.error) {
      return [
        {
          kind: "warning",
          code: mapBackendErrorToCode(response.error),
          requestCode: code ?? "",
        },
      ];
    }

    return [];
  };

  const handleMutation = (mutation) => {
    if (submitting) return;

    setSubmitting(true);

    mutation.mutate(
      { id: modalItem.item.id },
      {
        onSuccess: (response) => {
          queryClient.invalidateQueries({
            queryKey: ["fuel-requests"],
          });
          queryClient.invalidateQueries({
            queryKey: ["getInfo"],
          });

          const warnings = extractErrorsFromBulkResponse(
            response.data,
            modalItem.item.code
          );

          if (warnings.length) {
            setErrorQueue((prev) => [...prev, ...warnings]);
          }

          closeModal();
        },
        onError: () => {
          console.log("approve error");
          setErrorQueue((prev) => [
            ...prev,
            {
              kind: "error",
              code: "UNKNOWN",
              requestCode: "",
            },
          ]);
          closeModal();
        },
        onSettled: () => {
          setSubmitting(false);
        },
      }
    );
  };

  return (
    <Modal
      open={modalItem.open}
      onRequestClose={closeModal}
      modalHeading={`${t("main.fuel.fuelRequestsModal.titleSingle")}`}
      modalLabel={`${t("main.fuel.fuelRequestsModal.label")}`}
      primaryButtonText={`${t("main.fuel.fuelRequestsModal.approveSubmit")}`}
      secondaryButtonText={`${t("main.fuel.fuelRequestsModal.declineSubmit")}`}
      primaryButtonDisabled={submitting}
      onRequestSubmit={() => handleMutation(mutationApprove)}
      onSecondarySubmit={() => handleMutation(mutationDecline)}
      closeButtonLabel={`${t("main.modalText.close")}`}
    >
      <div className="request">
        <div className="request_item">
          <p>
            <User />
            <span>
              {t("main.fuel.fuelRequestsTable.client")}: {modalItem.item.client}
            </span>
          </p>
          <p>
            <Star />
            <span>
              {t("main.fuel.fuelRequestsTable.fuel")}: {modalItem.item.fuel}
            </span>
          </p>
        </div>
        <div className="request_item">
          <p>
            <Calendar />
            <span>
              {t("main.fuel.fuelRequestsTable.created")}:{" "}
              {formatDate(modalItem.item.created)}
            </span>
          </p>
          <p>
            <Parameter />
            <span>
              {" "}
              {t("main.fuel.fuelRequestsTable.code")}: {modalItem.item.code}
            </span>
          </p>
        </div>
      </div>
    </Modal>
  );
}
